import { useEffect, useState } from 'react';
import { apiService } from '../services/apiService';
import { formatBookingDate } from '../utils/bookingDates';
import { readSession } from '../config';

// Room rendering owns the active search. Imperative promo/auth callers share the
// same cached transport, so existing offer workflows do not need to be replaced.
export function useHotelSearch(state, dispatch) {
  const [result, setResult] = useState({ data: null, loading: false, error: null });
  const [revision, setRevision] = useState(0);
  const { hotelId, checkin, checkout, guestCount, promo_code } = state.searchState;
  let dateError = null;
  let startDate;
  let endDate;
  try {
    startDate = formatBookingDate(checkin);
    endDate = formatBookingDate(checkout);
  } catch (error) { dateError = error.message; }
  const roomKey = JSON.stringify(state.rooms);
  const userKey = readSession('userToken');
  useEffect(() => {
    let current = true;
    if (!hotelId || dateError) {
      setResult({ data: null, loading: false, error: dateError });
      return undefined;
    }
    setResult({ data: null, loading: true, error: null });
    dispatch({ type: 'SET_HOTEL_DATA', payload: null });
    const params = {
      startDate, endDate, rooms: JSON.parse(roomKey).length,
      guests: guestCount, roomsData: JSON.parse(roomKey), promo_code: promo_code || null,
    };
    const key = apiService.getHotelSearchKey(hotelId, params);
    // Invalidate old branding immediately, including during the debounce interval.
    apiService.latestSearchKey = key;
    const receive = event => {
      if (current && event.detail.key === key) {
        setResult({ data: event.detail.data, loading: false, error: null });
        dispatch({ type: 'SET_HOTEL_DATA', payload: event.detail.data });
      }
    };
    window.addEventListener('hotelSearchUpdate', receive);
    const timer = setTimeout(() => {
      apiService.getHotelData(hotelId, params).then(data => {
        if (current) {
          setResult({ data, loading: false, error: null });
          dispatch({ type: 'SET_HOTEL_DATA', payload: data });
        }
      }).catch(error => {
        if (current) setResult({ data: null, loading: false, error: error.message || 'Unable to load available rooms.' });
      });
    }, 300);
    return () => {
      current = false;
      clearTimeout(timer);
      window.removeEventListener('hotelSearchUpdate', receive);
    };
  }, [hotelId, startDate, endDate, roomKey, guestCount, promo_code, userKey, dateError, revision, dispatch]);
  return { ...result, retry: () => { apiService.clearPropertyCache(); setRevision(value => value + 1); } };
}
